import { useState } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';
import { getApiErrorMessage } from '../../../utils/authErrors';

export interface ImportHourlyDataInput {
  csvContent: string;
  tariffType: 'G11' | 'G12' | 'G12as' | 'G12w' | 'G13' | 'G13s';
  yearMonth?: string;
}

export interface ImportHourlyDataResult {
  success: boolean;
  message: string;
  importedMonths: number[];
  extrapolatedMonths: number[];
  totalRecords: number;
  importedHoursCount: number;
  extrapolatedHoursCount: number;
  patterns?: {
    weekday: number[];
    weekend: number[];
  };
  summary?: {
    totalConsumption: number;
    monthlyTotals: number[];
    avgDailyConsumption: number;
  };
  warnings?: string[];
  errors?: Array<{ yearMonth: string; error: string }>;
}

export interface UseImportHourlyDataReturn {
  importHourlyData: (input: ImportHourlyDataInput) => Promise<ImportHourlyDataResult>;
  loading: boolean;
  error: string | null;
  progress: number;
  reset: () => void;
}

export function useImportHourlyData(): UseImportHourlyDataReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const importHourlyData = async (input: ImportHourlyDataInput): Promise<ImportHourlyDataResult> => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      const client = getAmplifyClient();

      // Simulate progress updates
      setProgress(20); // Uploading

      const inputJson = JSON.stringify({
        csvContent: input.csvContent,
        tariffType: input.tariffType,
        yearMonth: input.yearMonth,
      });

      setProgress(40); // Validating

      const result = await client.mutations.importHourlyData({
        input: inputJson,
      });

      setProgress(60); // Processing

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data) {
        throw new Error('No data returned from import');
      }

      setProgress(80); // Extrapolating

      // Parse the AWSJSON response (it may be double-stringified by GraphQL)
      let parsedResult = JSON.parse(result.data as string);

      // If still a string after first parse, parse again (double-stringified response)
      if (typeof parsedResult === 'string') {
        parsedResult = JSON.parse(parsedResult);
      }

      const typedResult: ImportHourlyDataResult = parsedResult;

      setProgress(100); // Complete

      if (!typedResult.success) {
        throw new Error(typedResult.message || 'Import failed');
      }

      return typedResult;
    } catch (err) {
      setError(getApiErrorMessage(err));
      setProgress(0);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setLoading(false);
    setError(null);
    setProgress(0);
  };

  return {
    importHourlyData,
    loading,
    error,
    progress,
    reset,
  };
}
